// Inteligencia > Zonas de detección. El cliente dibuja sobre la foto de la cámara qué parte le
// interesa (o cuál ignorar: la calle, el edificio vecino). Lo que cae afuera no se cuenta en el
// Dashboard IA, los reportes ni las alertas. Regla: cuenta el punto de apoyo de cada recuadro
// (centro del borde inferior = pies / ruedas). Ver zonas.js.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { loadDays, addDays, todayKey, photoUrl, fmtHM, fmtDMY, fmtKey, dayKey } from './iaData';
import { APLICA, cuentaDeteccion, familiaZona, getZonas, guardarZonas, errorZonas, puntoApoyo } from './zonas';
import { T, Card, Btn } from './ui';

const MAX_ZONAS = 20;
const MAX_PUNTOS = 50;
const COLOR = { incluir: '#10b981', excluir: '#ef4444' };
const TITULO = { incluir: 'Detectar solo dentro de estas zonas', excluir: 'Ignorar estas zonas' };
const vacio = { incluir: [], excluir: [] };
const soloZonas = (d) => ({ incluir: d?.incluir || [], excluir: d?.excluir || [] });
const nuevoId = () => 'z' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const clamp = (v) => Math.min(1, Math.max(0, v));
const plural = (n, s, p) => `${n.toLocaleString('es-AR')} ${n === 1 ? s : p}`;
const fmtFecha = (iso) => { const ms = Date.parse(iso); return isNaN(ms) ? '' : `${fmtDMY(ms)} ${fmtHM(ms)}`; };

function contar(dets, zonas, photo) {
  const r = { personas: [0, 0], vehiculos: [0, 0] };
  for (const d of dets) r[familiaZona(d.cls)][cuentaDeteccion(d, zonas, photo) ? 0 : 1] += 1;
  return r;
}

export default function ZonasDeteccion({ customerId, camIdentifier, feedId, camName, onVerDashboard }) {
  const [guardado, setGuardado] = useState(null); // doc del servidor
  const [zs, setZs] = useState(vacio);
  const [hist, setHist] = useState([]);
  const [dibujo, setDibujo] = useState(null); // { tipo, puntos }
  const [sel, setSel] = useState(null); // { tipo, id }
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [fotos, setFotos] = useState(null); // fotos con recuadros del último día que tiene
  const [idx, setIdx] = useState(0);
  const [url, setUrl] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const [ancho, setAncho] = useState(800);
  const svgRef = useRef(null);
  const boxRef = useRef(null);
  const drag = useRef(null);

  // Config guardada
  useEffect(() => {
    let alive = true;
    setError(null);
    getZonas(feedId, { fresco: true })
      .then((d) => { if (!alive) return; setGuardado(d); setZs(soloZonas(d)); setHist([]); })
      .catch((e) => alive && setError(errorZonas(e)));
    return () => { alive = false; };
  }, [feedId]);

  // Fotos de hoy (o del último día con detecciones, hasta 7 días atrás) SIN filtrar, para la vista previa
  useEffect(() => {
    let alive = true;
    (async () => {
      for (let i = 0; i < 7 && alive; i += 2) {
        const dias = [addDays(todayKey(), -i), addDays(todayKey(), -i - 1)];
        let ph = [];
        try { ph = await loadDays(camIdentifier, customerId, dias, null, { sinZonas: true }); } catch (e) { /* sigue */ }
        const conCajas = ph.filter((p) => p.hasBoxes);
        if (conCajas.length) {
          const ultimoDia = dayKey(conCajas[conCajas.length - 1].ms);
          const delDia = conCajas.filter((p) => dayKey(p.ms) === ultimoDia);
          if (!alive) return;
          setFotos(delDia);
          // arranca en la última foto con algo detectado (si no hay, la última)
          let k = delDia.length - 1;
          for (let j = delDia.length - 1; j >= 0; j -= 1) if (delDia[j].dets.length) { k = j; break; }
          setIdx(k);
          return;
        }
      }
      if (alive) setFotos([]);
    })();
    return () => { alive = false; };
  }, [camIdentifier, customerId]);

  const foto = fotos && fotos[idx];
  useEffect(() => {
    if (!foto) return undefined;
    let alive = true;
    setImgErr(false);
    photoUrl(foto).then((u) => alive && setUrl(u)).catch(() => alive && setImgErr(true));
    return () => { alive = false; };
  }, [foto]);

  // Ancho real del lienzo (para que los puntos tengan el mismo tamaño en pantalla en PC y celular)
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setAncho(el.clientWidth || 800));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fotos]);

  const W = foto?.w || 1920;
  const H = foto?.h || 1080;
  const esc = W / Math.max(1, ancho); // píxeles de imagen por píxel de pantalla
  const angosto = ancho < 600;
  const rPunto = (angosto ? 11 : 7) * esc;

  // ---------- edición con historial ----------
  const snap = () => setHist((h) => [...h.slice(-49), JSON.stringify({ zs, dibujo })]);
  const cambiar = (fn) => { snap(); setZs((z) => fn(z)); };
  const deshacer = () => {
    if (!hist.length) return;
    const prev = JSON.parse(hist[hist.length - 1]);
    setHist(hist.slice(0, -1));
    setZs(prev.zs); setDibujo(prev.dibujo);
  };
  const restablecer = () => { snap(); setZs(soloZonas(guardado)); setDibujo(null); setSel(null); };
  const total = zs.incluir.length + zs.excluir.length;
  const sucio = JSON.stringify(zs) !== JSON.stringify(soloZonas(guardado));

  const empezar = (tipo) => { snap(); setSel(null); setDibujo({ tipo, puntos: [] }); };
  const cerrar = useCallback(() => {
    if (!dibujo || dibujo.puntos.length < 3) return;
    const lista = zs[dibujo.tipo];
    const z = { id: nuevoId(), nombre: `${dibujo.tipo === 'incluir' ? 'Zona' : 'Ignorar'} ${lista.length + 1}`, puntos: dibujo.puntos, aplica: 'todo' };
    snap();
    setZs({ ...zs, [dibujo.tipo]: [...lista, z] });
    setSel({ tipo: dibujo.tipo, id: z.id });
    setDibujo(null);
  }, [dibujo, zs]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelar = () => { snap(); setDibujo(null); };

  useEffect(() => {
    const k = (e) => {
      if (e.target && /input|select|textarea/i.test(e.target.tagName)) return;
      if (e.key === 'Escape' && dibujo) setDibujo(null);
      if (e.key === 'Enter' && dibujo) cerrar();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); deshacer(); }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });

  const aNorm = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height)];
  };
  const distPx = (a, b) => { const r = svgRef.current.getBoundingClientRect(); return Math.hypot((a[0] - b[0]) * r.width, (a[1] - b[1]) * r.height); };

  const onDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const p = aNorm(e);
    const t = e.target;
    if (dibujo) {
      e.preventDefault();
      const pts = dibujo.puntos;
      if (pts.length >= 3 && distPx(p, pts[0]) < (angosto ? 22 : 14)) return cerrar();
      if (pts.length && distPx(p, pts[pts.length - 1]) < 4) return; // doble clic: no repetir el punto
      if (pts.length >= MAX_PUNTOS) { toast.info(`Cada zona puede tener hasta ${MAX_PUNTOS} puntos`); return; }
      snap();
      setDibujo({ ...dibujo, puntos: [...pts, p] });
      return;
    }
    const vi = t.getAttribute && t.getAttribute('data-vi');
    const zid = t.getAttribute && t.getAttribute('data-zid');
    const tipo = t.getAttribute && t.getAttribute('data-tipo');
    if (vi != null && zid) {
      e.preventDefault();
      snap();
      drag.current = { tipo, id: zid, i: Number(vi) };
      setSel({ tipo, id: zid });
      try { svgRef.current.setPointerCapture(e.pointerId); } catch (er) { /* */ }
      return;
    }
    if (zid) { setSel({ tipo, id: zid }); return; }
    setSel(null);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    e.preventDefault();
    const p = aNorm(e);
    setZs((z) => ({ ...z, [d.tipo]: z[d.tipo].map((x) => (x.id === d.id ? { ...x, puntos: x.puntos.map((q, j) => (j === d.i ? p : q)) } : x)) }));
  };
  const onUp = () => { drag.current = null; };

  const editar = (tipo, id, patch) => cambiar((z) => ({ ...z, [tipo]: z[tipo].map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  const borrar = (tipo, id) => { cambiar((z) => ({ ...z, [tipo]: z[tipo].filter((x) => x.id !== id) })); if (sel?.id === id) setSel(null); };

  const guardar = async () => {
    if (dibujo) { toast.info('Cerrá o cancelá la zona que estás dibujando'); return; }
    setGuardando(true);
    try {
      const d = await guardarZonas(feedId, zs);
      setGuardado(d); setZs(soloZonas(d)); setHist([]);
      toast.success(total ? 'Zonas guardadas. Desde ahora la IA cuenta solo lo que está en el área elegida.' : 'Zonas borradas. La IA vuelve a contar toda la imagen.');
    } catch (e) {
      toast.error(errorZonas(e));
    }
    setGuardando(false);
  };

  // ---------- vista previa ----------
  const cuentaFoto = useMemo(() => (foto ? contar(foto.dets, zs, foto) : null), [foto, zs]);
  const cuentaDia = useMemo(() => {
    if (!fotos || !fotos.length) return null;
    let si = 0, no = 0;
    for (const f of fotos) for (const d of f.dets) { if (cuentaDeteccion(d, zs, f)) si += 1; else no += 1; }
    return { si, no, n: fotos.length };
  }, [fotos, zs]);

  const pts = (l) => l.map(([x, y]) => `${x * W},${y * H}`).join(' ');
  const sw = 2.5 * esc;

  if (!feedId) return <Card title="Zonas de detección"><div style={{ color: T.muted }}>Esta cámara no tiene identificador de feed: no se pueden configurar zonas.</div></Card>;

  return (
    <div style={{ background: T.bg, color: T.text, borderRadius: 24, padding: 'clamp(12px, 2vw, 22px)' }}>
      <style>{`
        .zd-grid{display:grid;grid-template-columns:minmax(0,1.7fr) minmax(280px,1fr);gap:16px;align-items:start}
        @media (max-width:980px){.zd-grid{grid-template-columns:1fr}}
        .zd-tool{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:10px}
        .zd-in{width:100%;border:1px solid ${T.line};border-radius:8px;padding:6px 8px;font-size:13px;background:#fff;color:${T.text}}
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Zonas de detección <span style={{ fontWeight: 400, fontStyle: 'italic', color: T.muted }}>{camName || camIdentifier}</span></div>
          <div style={{ fontSize: 13, color: T.muted, maxWidth: 760 }}>
            Marcá sobre la imagen qué parte te interesa. Lo que la IA detecte afuera (la calle, un edificio vecino) no se cuenta en el Dashboard IA, los reportes ni las alertas.
          </div>
        </div>
        {onVerDashboard && <Btn onClick={onVerDashboard}>Ver Dashboard IA</Btn>}
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 12, padding: 12, marginBottom: 12 }}>No se pudo leer la configuración: {error}</div>}

      <div className="zd-grid">
        <Card
          title={foto ? <>Foto del {fmtKey(dayKey(foto.ms))} · {fmtHM(foto.ms)}</> : 'Imagen de la cámara'}
          right={fotos && fotos.length > 1 && (
            <>
              <Btn disabled={idx <= 0} onClick={() => setIdx(idx - 1)} title="Foto anterior">‹</Btn>
              <span style={{ fontSize: 12, color: T.muted }}>{idx + 1} / {fotos.length}</span>
              <Btn disabled={idx >= fotos.length - 1} onClick={() => setIdx(idx + 1)} title="Foto siguiente">›</Btn>
            </>
          )}
        >
          <div className="zd-tool">
            {dibujo ? (
              <>
                <span style={{ fontSize: 13, fontWeight: 600, color: COLOR[dibujo.tipo] }}>
                  {dibujo.puntos.length < 3 ? `Tocá la imagen para marcar los puntos (${dibujo.puntos.length} de al menos 3)` : `${dibujo.puntos.length} puntos · doble clic o tocá el primer punto para cerrar`}
                </span>
                <Btn active disabled={dibujo.puntos.length < 3} onClick={cerrar}>Cerrar zona</Btn>
                <Btn onClick={cancelar}>Cancelar</Btn>
              </>
            ) : (
              <>
                <Btn disabled={total >= MAX_ZONAS} onClick={() => empezar('incluir')} style={{ borderColor: COLOR.incluir, color: '#047857' }}>+ Zona a detectar</Btn>
                <Btn disabled={total >= MAX_ZONAS} onClick={() => empezar('excluir')} style={{ borderColor: COLOR.excluir, color: '#b91c1c' }}>+ Zona a ignorar</Btn>
              </>
            )}
            <Btn disabled={!hist.length} onClick={deshacer} title="Deshacer (Ctrl+Z)">↶ Deshacer</Btn>
            <Btn disabled={!sucio && !dibujo} onClick={restablecer} title="Volver a lo último guardado">Restablecer</Btn>
          </div>

          <div ref={boxRef} style={{ position: 'relative', width: '100%', aspectRatio: `${W} / ${H}`, background: '#111827', borderRadius: 12, overflow: 'hidden', userSelect: 'none' }}>
            {url && !imgErr && <img src={url} alt="Última foto de la cámara" onError={() => setImgErr(true)} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none' }} />}
            {(fotos === null || (foto && !url && !imgErr)) && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#e5e7eb', fontSize: 13 }}>Cargando la foto…</div>}
            {(fotos && !fotos.length) && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#e5e7eb', fontSize: 13, padding: 16, textAlign: 'center' }}>No hay fotos con detecciones en los últimos días. Igual podés dibujar las zonas.</div>}
            {imgErr && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#e5e7eb', fontSize: 13 }}>No se pudo cargar la foto</div>}
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onDoubleClick={(e) => { if (dibujo) { e.preventDefault(); cerrar(); } }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: dibujo || sel ? 'none' : 'pan-y', cursor: dibujo ? 'crosshair' : 'default' }}
            >
              <defs>
                <pattern id="zd-rayas" width={16 * esc} height={16 * esc} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width={16 * esc} height={16 * esc} fill="rgba(239,68,68,.18)" />
                  <line x1="0" y1="0" x2="0" y2={16 * esc} stroke="rgba(239,68,68,.55)" strokeWidth={5 * esc} />
                </pattern>
              </defs>

              {/* Recuadros de la foto: verdes cuentan, grises tachados se ignoran */}
              {foto && foto.dets.map((d, i) => {
                if (d.x1 == null) return null;
                const ok = cuentaDeteccion(d, zs, foto);
                const [px, py] = puntoApoyo(d, W, H);
                const c = ok ? '#22c55e' : '#9ca3af';
                return (
                  <g key={i} pointerEvents="none" opacity={ok ? 1 : 0.85}>
                    <rect x={d.x1} y={d.y1} width={d.x2 - d.x1} height={d.y2 - d.y1} fill={ok ? 'rgba(34,197,94,.12)' : 'rgba(17,24,39,.25)'} stroke={c} strokeWidth={sw} strokeDasharray={ok ? undefined : `${6 * esc} ${4 * esc}`} />
                    {!ok && <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={c} strokeWidth={sw} />}
                    <circle cx={px * W} cy={py * H} r={3.5 * esc} fill={c} stroke="#fff" strokeWidth={1 * esc} />
                  </g>
                );
              })}

              {['excluir', 'incluir'].map((tipo) => zs[tipo].map((z) => {
                const activa = sel && sel.id === z.id;
                return (
                  <g key={z.id}>
                    <polygon
                      data-zid={z.id} data-tipo={tipo}
                      points={pts(z.puntos)}
                      fill={tipo === 'excluir' ? 'url(#zd-rayas)' : 'rgba(16,185,129,.16)'}
                      stroke={COLOR[tipo]} strokeWidth={(activa ? 4 : 2.5) * esc}
                      style={{ cursor: dibujo ? 'crosshair' : 'pointer' }}
                    />
                    {activa && !dibujo && z.puntos.map(([x, y], i) => (
                      <circle key={i} data-zid={z.id} data-tipo={tipo} data-vi={i} cx={x * W} cy={y * H} r={rPunto} fill="#fff" stroke={COLOR[tipo]} strokeWidth={2.5 * esc} style={{ cursor: 'move' }} />
                    ))}
                    {!dibujo && (() => {
                      const cx = z.puntos.reduce((a, p) => a + p[0], 0) / z.puntos.length;
                      const cy = z.puntos.reduce((a, p) => a + p[1], 0) / z.puntos.length;
                      return <text x={cx * W} y={cy * H} textAnchor="middle" dominantBaseline="middle" fontSize={13 * esc} fontWeight="700" fill="#fff" stroke="rgba(0,0,0,.55)" strokeWidth={3 * esc} paintOrder="stroke" pointerEvents="none">{z.nombre}</text>;
                    })()}
                  </g>
                );
              }))}

              {dibujo && (
                <g pointerEvents="none">
                  {dibujo.puntos.length > 1 && <polyline points={pts(dibujo.puntos)} fill={dibujo.tipo === 'excluir' ? 'rgba(239,68,68,.15)' : 'rgba(16,185,129,.15)'} stroke={COLOR[dibujo.tipo]} strokeWidth={2.5 * esc} strokeDasharray={`${8 * esc} ${5 * esc}`} />}
                  {dibujo.puntos.map(([x, y], i) => <circle key={i} cx={x * W} cy={y * H} r={i === 0 && dibujo.puntos.length >= 3 ? rPunto * 1.4 : rPunto * 0.8} fill={i === 0 ? COLOR[dibujo.tipo] : '#fff'} stroke={COLOR[dibujo.tipo]} strokeWidth={2 * esc} />)}
                </g>
              )}
            </svg>
          </div>

          {cuentaFoto && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 13 }}>
              <span><b style={{ color: '#047857' }}>Se cuentan {plural(cuentaFoto.personas[0], 'persona', 'personas')}</b> · <span style={{ color: T.muted }}>se ignoran {cuentaFoto.personas[1]}</span></span>
              <span><b style={{ color: '#047857' }}>Se cuentan {plural(cuentaFoto.vehiculos[0], 'vehículo o máquina', 'vehículos y máquinas')}</b> · <span style={{ color: T.muted }}>se ignoran {cuentaFoto.vehiculos[1]}</span></span>
            </div>
          )}
          {cuentaDia && (
            <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
              En todo el día ({plural(cuentaDia.n, 'foto', 'fotos')}): se cuentan {cuentaDia.si.toLocaleString('es-AR')} detecciones y se ignoran {cuentaDia.no.toLocaleString('es-AR')}.
            </div>
          )}
          <div style={{ fontSize: 12, color: T.muted, marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid #22c55e', marginRight: 4, verticalAlign: -1 }} />se cuenta</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, border: '2px dashed #9ca3af', marginRight: 4, verticalAlign: -1 }} />se ignora</span>
            <span>● punto que decide: el centro del borde de abajo del recuadro (donde la persona o el vehículo toca el piso)</span>
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {['incluir', 'excluir'].map((tipo) => (
            <Card key={tipo} title={<><span style={{ width: 10, height: 10, borderRadius: 3, background: COLOR[tipo], display: 'inline-block' }} />{TITULO[tipo]}</>}>
              {!zs[tipo].length && (
                <div style={{ fontSize: 13, color: T.muted }}>
                  {tipo === 'incluir' ? 'Sin zonas: se detecta en toda la imagen.' : 'Ninguna. Usalas para tapar la calle, la vereda o un edificio vecino.'}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {zs[tipo].map((z) => (
                  <div key={z.id} onClick={() => !dibujo && setSel({ tipo, id: z.id })} style={{ border: `1px solid ${sel?.id === z.id ? COLOR[tipo] : T.line}`, borderRadius: 12, padding: 10, background: sel?.id === z.id ? COLOR[tipo] + '0d' : '#fff', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input className="zd-in" value={z.nombre} maxLength={60} aria-label="Nombre de la zona" onFocus={snap} onChange={(e) => setZs((s) => ({ ...s, [tipo]: s[tipo].map((x) => (x.id === z.id ? { ...x, nombre: e.target.value } : x)) }))} />
                      <button type="button" onClick={(e) => { e.stopPropagation(); borrar(tipo, z.id); }} title="Eliminar zona" style={{ border: `1px solid ${T.line}`, background: '#fff', color: T.red, borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Eliminar</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 12, color: T.muted }}>
                      <span style={{ whiteSpace: 'nowrap' }}>Aplica a</span>
                      <select className="zd-in" value={z.aplica || 'todo'} onChange={(e) => editar(tipo, z.id, { aplica: e.target.value })}>
                        {APLICA.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                      <span style={{ whiteSpace: 'nowrap' }}>{z.puntos.length} puntos</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}

          <Card>
            <Btn active disabled={guardando || !!dibujo || !sucio || !guardado && !!error} onClick={guardar} style={{ width: '100%', padding: '10px 12px', fontSize: 14 }}>
              {guardando ? 'Guardando…' : sucio ? 'Guardar zonas' : 'Sin cambios para guardar'}
            </Btn>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
              {guardado?.updatedAt ? <>Última edición: {guardado.updatedBy || '—'} · {fmtFecha(guardado.updatedAt)}</> : 'Todavía no hay zonas guardadas para esta cámara.'}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
              Para mover un punto, elegí la zona y arrastralo. Hasta {MAX_ZONAS} zonas por cámara. Se aplica a las fotos que ya están analizadas y a las nuevas; las fotos anteriores al 30/05/2026 no tienen recuadros y se cuentan completas.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

